import { useState, useMemo, useRef, useEffect } from "react";
import { formatExpiryDate } from "../../utils/formatters";
import styles from "./ProductSearchModal.module.css";

// mode: "add" | "remove" | "workOrder" | "delivery" | "edit"
export default function ProductSearchModal({ products, mode = "add", onSelect, onCreateNew, onClose }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const inputRef = useRef(null);
  const isRemoval = ["remove", "workOrder", "delivery"].includes(mode);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Group products by barcode so multi-lot items appear as one row
  const grouped = useMemo(() => {
    const filtered = products.filter(p =>
      (!typeFilter || p.type === typeFilter) &&
      (!search || p.description.toLowerCase().includes(search.toLowerCase()) ||
       p.manufacturer.toLowerCase().includes(search.toLowerCase()) ||
       (p.barcode || p.id).toLowerCase().includes(search.toLowerCase()))
    );

    const map = {};
    for (const p of filtered) {
      const key = p.barcode || p.id;
      if (!map[key]) {
        map[key] = { barcode: key, description: p.description, manufacturer: p.manufacturer, type: p.type, lots: [] };
      }
      map[key].lots.push(p);
    }

    // Sort lots within each group by expiry (FEFO)
    for (const g of Object.values(map)) {
      g.lots.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || ""));
      g.totalStock = g.lots.reduce((sum, l) => sum + (l.stock || 0), 0);
    }

    return Object.values(map).slice(0, 50);
  }, [products, search, typeFilter]);

  const handleSelect = (group) => {
    if (isRemoval && group.totalStock === 0) return;
    onSelect(group.lots);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.title}>Search Products</div>
          <button onClick={onClose} className={styles.closeBtn}>x</button>
        </div>

        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, manufacturer, or barcode..."
          className={styles.searchInput}
        />

        <div className={styles.filterGroup}>
          {["", "Drug", "Supply"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={typeFilter === t ? styles.filterBtnActive : styles.filterBtn}>
              {t || "All"}
            </button>
          ))}
        </div>

        <div className={styles.resultsList}>
          {grouped.length === 0 && search && (
            <div className={styles.noResults}>No products found</div>
          )}

          {grouped.map(g => {
            const isDimmed = isRemoval && g.totalStock === 0;
            return (
              <button
                key={g.barcode}
                className={`${styles.resultCard} ${isDimmed ? styles.resultCardDimmed : ""}`}
                onClick={() => handleSelect(g)}
                disabled={isDimmed}
              >
                <div className={styles.resultInfo}>
                  <div className={styles.resultName}>{g.description}</div>
                  <div className={styles.resultMeta}>
                    {g.manufacturer}
                    {g.lots.length > 1 && <span className={styles.lotsBadge}>{g.lots.length} lots</span>}
                  </div>
                </div>
                <div className={styles.resultStock}>
                  <div className={
                    g.totalStock > 10 ? styles.stockGood :
                    g.totalStock > 0 ? styles.stockWarn : styles.stockDanger
                  }>
                    {g.totalStock}
                  </div>
                  <div className={styles.stockLabel}>stock</div>
                </div>
              </button>
            );
          })}
        </div>

        {mode === "add" && onCreateNew && (
          <button onClick={onCreateNew} className={styles.createNewBtn}>
            + CREATE NEW PRODUCT
          </button>
        )}
      </div>
    </div>
  );
}
